import React, { useMemo } from "react"
import { Spinner } from "reactstrap"
import { EngDiagQuestionView } from "./EngDiagQuestionView"
import {
  getEngDiagQuestion,
  getEngDiagSessionState,
  skipEngDiagQuestion,
  submitEngDiagAnswer,
  type EngDiagSessionRef,
} from "../services/eng-diag.service"
import {
  useEngAttemptRunner,
  type EngAttemptTransport,
} from "../hooks/useEngAttemptRunner"
import {
  ENG_DIAG_COPY as COPY,
  SHOW_SKILL_PROVENANCE,
} from "../../../constants/diagnostics/eng-diag.constants"
import type { EngDiagSessionState } from "../types/eng-diag.types"
import styles from "./eng-diag-player.module.css"
import theme from "./eng-diag-theme.module.css"

type Props = {
  /** The lesson activity the student opened. */
  activityKey: string
  /** Called once the last question is answered, so the page can show the finish screen. */
  onFinished?: (state: EngDiagSessionState) => void
}

/**
 * Takes a student through an English diagnostics test, one question at a time.
 *
 * The server decides what comes next: every answer returns the next question's key, and the
 * session state is the source of truth for progress. Nothing about the test is held locally, so
 * closing the tab and coming back resumes exactly where it stopped.
 *
 * The question-serving loop itself lives in useEngAttemptRunner, shared with the Skill Builder
 * player - it is the same machinery and carries the same hard-won fixes. What stays here is the
 * chrome: the cluster pill, the progress line, and the fact that a diagnostic shows NO VERDICT.
 *
 * The student is never told whether an answer was right. That is deliberate - a diagnostic
 * measures what they can do, and a running score changes how they answer the rest of it. The
 * runner is therefore used WITHOUT holdFeedback, and the server does not send one.
 */
export function EngDiagAttemptPlayer({ activityKey, onFinished }: Props) {
  // Memoised, and it has to be: the runner's load() depends on the transport, and its first
  // effect depends on load. A transport rebuilt every render would re-run that effect every
  // render, which is a refetch loop rather than a stale closure.
  const transport: EngAttemptTransport<EngDiagSessionState> = useMemo(() => {
    const ref: EngDiagSessionRef = { activity_key: activityKey }
    return {
      getState: signal => getEngDiagSessionState(ref, signal),
      readProgress: state => ({
        answered: state.answered_questions ?? 0,
        total: state.total_questions ?? 0,
      }),
      readCurrentKey: state => state.current_question_key,
      getQuestion: (questionKey, signal) =>
        getEngDiagQuestion({ ...ref, session_question_key: questionKey }, signal),
      submitAnswer: ({ questionKey, response, durationSeconds }) =>
        submitEngDiagAnswer({
          ...ref,
          session_question_key: questionKey,
          response,
          duration_seconds: durationSeconds,
        }),
      skipQuestion: ({ questionKey, reason, durationSeconds }) =>
        skipEngDiagQuestion({
          ...ref,
          session_question_key: questionKey,
          skip_reason: reason,
          duration_seconds: durationSeconds,
        }),
    }
  }, [activityKey])

  const {
    loading,
    busy,
    error,
    state,
    question,
    answers,
    setAnswers,
    canSubmit,
    answerParts,
    secondsLeft,
    progress,
    submit,
    skip,
  } = useEngAttemptRunner<EngDiagSessionState>({ transport, copy: COPY, onFinished })

  /**
   * The part of English this question belongs to, for the pill on the card.
   *
   * Read off the session's own block list rather than off `question.provenance`: provenance is
   * the temporary review aid and goes away with SHOW_SKILL_PROVENANCE, while the block a
   * question was served from is how the test is built and is not going anywhere. It is also the
   * same name the welcome screen already shows the student, so the two agree.
   */
  const cluster = useMemo(() => {
    if (!state?.current_topic_key) return null
    const block = state.topics?.find(
      topic => topic.pk_session_topic_key === state.current_topic_key,
    )
    return block?.cluster_name || null
  }, [state])

  if (loading) {
    return (
      <div className={`${theme.engDiagTheme} text-center py-5`}>
        <Spinner color="primary" />
        <div className={`mt-3 ${styles.engDiagNoticeBody}`}>{COPY.LOADING}</div>
      </div>
    )
  }

  if (error && !question) {
    return (
      <div className={`${theme.engDiagTheme} ${styles.engDiagNotice}`}>
        <p className={styles.engDiagError}>{error}</p>
      </div>
    )
  }

  if (!question) {
    return (
      <div className={`${theme.engDiagTheme} ${styles.engDiagNotice}`}>
        <div className={styles.engDiagNoticeTitle}>{COPY.FINISH_TITLE}</div>
        <p className={styles.engDiagNoticeBody}>{COPY.FINISH_NOTE}</p>
      </div>
    )
  }

  const { answered, total } = progress
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0

  return (
    <div className={`${theme.engDiagTheme} assessement-section summary-width-hold`}>
      <div className={styles.engDiagCard}>
        {/* ONE header line: strand, what the question is testing, and how far through the
            student is. These were a separate progress card stacked above the question card,
            which cost about 110px of height - enough that a four-picture question pushed its
            last two options below the fold and a child had to scroll to find out they were
            there. Everything here is glanceable rather than read, so it belongs on one line. */}
        <div className={styles.engDiagCardHead}>
          {/* Which part of English this question belongs to. The cluster and nothing finer:
              the welcome screen already lists the clusters by name, so this tells a student
              nothing new, where the topic or the skill would hint at the answer and the YEAR
              is the one thing the player must never reveal. */}
          {cluster ? (
            <span className={styles.engDiagClusterPill}>
              <i className="fas fa-pen-nib" aria-hidden="true" />
              {cluster}
            </span>
          ) : null}

          {/* TEMPORARY review aid - see SHOW_SKILL_PROVENANCE. Shows the curriculum team what
              each question is testing. Off for real students: it names the YEAR, which the
              rest of the player deliberately hides. */}
          {SHOW_SKILL_PROVENANCE && (question.provenance || question.id) ? (
            <div className={styles.engDiagProvenance}>
              {/* Curriculum order, outside in, with the skill LAST: the path is context and
                  the skill is the thing actually being read. Leading with the skill made the
                  line look scrambled - the PM read
                  "...dictated words - 3 · Year 4 · Spelling · Statutory word lists · [Y3/4
                  list] Part 3" as a shuffle, when every part was in its right place.
                  `topic` is dropped: the subtopic names it closely enough, and the line has to
                  sit beside the progress bar without wrapping. */}
              {question.provenance ? (
                <>
                <span className="text-muted">
                  {[
                    question.provenance.year,
                    question.provenance.cluster,
                    question.provenance.subtopic,
                  ]
                    .filter(Boolean)
                    .map(part => `${part} · `)
                    .join("")}
                </span>
                <span className="fw-700">{question.provenance.skill}</span>
                </>
              ) : null}
              {/* Last, and quiet: it is for whoever is reading over the child's
                  shoulder, not for the child. `select-all` because a 12-character mixed-case
                  key is the kind of string that gets copied wrong by hand. */}
              {question.id ? (
                <span className={styles.engDiagQuestionId} title="Question ID">
                  {question.id}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className={styles.engDiagProgress}>
            <span className={styles.engDiagProgressCount}>
              <span className={styles.engDiagProgressCountValue}>{answered}</span>/{total}
              {/* Progress is by COUNT, never by position in a list: a skipped question comes
                  back, so "question 3 of 10" would go backwards. answered + 1 is "the one you
                  are on", which only ever stands still or goes up. */}
              <span className={styles.engDiagProgressQuestion}>
                {COPY.QUESTION_LABEL_SHORT}
                {Math.min(answered + 1, Math.max(total, 1))}
              </span>
            </span>
            <div
              className={styles.engDiagProgressTrack}
              role="progressbar"
              aria-label={`${answered} of ${total} ${COPY.ANSWERED_SUFFIX}`}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              {/* A sliver at zero, so the bar reads as a bar before the first answer lands. */}
              <div
                className={styles.engDiagProgressFill}
                style={{ width: `${Math.max(percent, 1)}%` }}
              />
            </div>
            {secondsLeft !== null && (
              <span
                className={`${styles.engDiagTimer} ${
                  secondsLeft <= 10 ? styles.engDiagTimerLow : ""
                }`}
              >
                {secondsLeft}s
              </span>
            )}
          </div>
        </div>

        <EngDiagQuestionView
          question={question}
          value={answers}
          onChange={setAnswers}
        />

        {error && <p className={styles.engDiagError}>{error}</p>}

        <div className={styles.engDiagFooter}>
          <button
            type="button"
            className={styles.engDiagSkip}
            onClick={() => void skip("MANUAL")}
            disabled={busy}
          >
            {COPY.SKIP_FOR_NOW}
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
          {/* An empty submission is marked WRONG, so letting it through costs a child a
              mark for a mis-click. "Skip for now" is the way past a question they cannot do,
              and that is not marked against them.

              Submit waits for EVERY part, so a part-finished question needs to say which ones
              are outstanding - otherwise the button just sits there grey after the child has
              answered something, which reads as a bug. */}
          <div className={styles.engDiagSubmitRow}>
            {!canSubmit && (
              <span className={styles.engDiagSubmitHint}>
                {answerParts.answered > 0 && answerParts.total > 1
                  ? COPY.PARTS_ANSWERED(answerParts.answered, answerParts.total)
                  : COPY.ANSWER_TO_CONTINUE}
              </span>
            )}
            <button
              type="button"
              className={styles.engDiagSubmit}
              onClick={() => void submit()}
              disabled={busy || !canSubmit}
              title={canSubmit ? undefined : COPY.ANSWER_BEFORE_SUBMIT}
            >
              {busy ? COPY.SUBMITTING : COPY.SUBMIT_ANSWER}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EngDiagAttemptPlayer
